/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ApolloError, gql, useMutation, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Spinner,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import { Form } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLModal } from 'src/components/common/URLModal';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionBlock } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useDebouncedEffect } from 'src/hooks/useDebouncedEffect';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import {
  buildStatsMap,
  sortByStatsData,
} from '../variantChecksConfigPage/common/helpers';

import { ConfigureExcludedClientsModal } from './configureExcludedClientsModal/ConfigureExcludedClientsModal';
import {
  AUTOSAVE_TIMEOUT_MS,
  ChecksTimeRange,
  STATS_TIME_RANGE,
  checksTimeRangeInSeconds,
} from './constants';
import { ExcludedClientsTable } from './excludedClientsTable/ExcludedClientsTable';
import { ExcludedOperationsTable } from './excludedOperationsTable/ExcludedOperationsTable';
import { IgnoredConditionsCard } from './ignoredConditionsCard/IgnoredConditionsCard';
import {
  ExcludedClientsClientFilterFragment,
  ExcludedOperationNameFilterFragment,
  removeTypename,
  removeTypenameFromExcludedOperationNames,
} from './transforms';
import { VariantCheckbox } from './variantCheckbox/VariantCheckbox';

export const CheckConfigFragment = gql`
  fragment CheckConfigFragment on CheckConfiguration {
    id
    includeBaseVariant
    includedVariants
    operationCountThreshold
    timeRangeSeconds
    downgradeStaticChecks
    downgradeDefaultValueChange
    updatedAt
    updatedBy {
      id
      ... on User {
        fullName
      }
    }
    excludedClients {
      ...ExcludedClientsClientFilterFragment
    }
    excludedOperationNames {
      ...ExcludedOperationNameFilterFragment
    }
  }
  ${ExcludedClientsClientFilterFragment}
  ${ExcludedOperationNameFilterFragment}
`;

const checksConfigPageQuery = gql`
  query ChecksConfigPageQuery($graphId: ID!) {
    service(id: $graphId) {
      id
      title
      roles {
        canQueryCheckConfiguration
        canWriteCheckConfiguration
      }
      variants {
        id
        name
      }
      checkConfiguration {
        id
        ...CheckConfigFragment
      }
    }
  }
  ${CheckConfigFragment}
`;

export const checksConfigPageStatsQuery = gql`
  query ChecksConfigPageStatsQuery($graphId: ID!, $timeFrom: Timestamp!) {
    service(id: $graphId) {
      id
      account {
        id
      }
      operationCheckRequestsByVariant(from: $timeFrom) {
        cachedRequestsCount
        uncachedRequestsCount
        variant
      }
    }
  }
`;

const updateCheckConfigMutation = gql`
  mutation UpdateCheckConfigMutation(
    $graphId: ID!
    $includeBaseVariant: Boolean!
    $includedVariants: [String!]
    $operationCountThreshold: Int!
    $timeRangeSeconds: Long!
    $excludedClients: [ClientFilterInput!]
    $excludedOperationNames: [OperationNameFilterInput!]
    $downgradeStaticChecks: Boolean
    $downgradeDefaultValueChange: Boolean
  ) {
    service(id: $graphId) {
      updateCheckConfiguration(
        includeBaseVariant: $includeBaseVariant
        includedVariants: $includedVariants
        operationCountThreshold: $operationCountThreshold
        timeRangeSeconds: $timeRangeSeconds
        excludedClients: $excludedClients
        excludedOperationNames: $excludedOperationNames
        downgradeStaticChecks: $downgradeStaticChecks
        downgradeDefaultValueChange: $downgradeDefaultValueChange
      ) {
        id
        ...CheckConfigFragment
      }
    }
  }
  ${CheckConfigFragment}
`;

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  includeBaseVariant: Yup.boolean().required(),
  includedVariants: Yup.array(Yup.string()),
  operationCountThreshold: Yup.number().min(1, 'min: 1').required('required'),
  timeRangeSeconds: Yup.mixed()
    .oneOf(Object.values(checksTimeRangeInSeconds))
    .required(),
  excludedClients: Yup.array(
    Yup.object({
      name: Yup.string(),
      version: Yup.string().nullable(),
    }),
  ).nullable(),
  excludedOperationNames: Yup.array(
    Yup.object({
      name: Yup.string(),
    }),
  ).nullable(),
  downgradeStaticChecks: Yup.boolean().required().nullable(),
  downgradeDefaultValueChange: Yup.boolean().required().nullable(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

export const ChecksConfigPage = ({ graphId }: { graphId: string }) => {
  const shouldShowDowngradeChecksConfig = useLDFlag(
    'pulsar-show-downgrade-checks-config',
  );

  const location = useLocation();

  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    accountId: currentAccountId as string,
    skip: !currentAccountId,
  });

  const { data, loading, error } = useQuery<
    GraphQLTypes.ChecksConfigPageQuery,
    GraphQLTypes.ChecksConfigPageQueryVariables
  >(checksConfigPageQuery, {
    variables: {
      graphId,
    },
    context: appLinkContext({
      catchErrors: [ignorePermissionsErrors],
    }),
  });

  const { data: statsData, loading: statsLoading } = useQuery<
    GraphQLTypes.ChecksConfigPageStatsQuery,
    GraphQLTypes.ChecksConfigPageStatsQueryVariables
  >(checksConfigPageStatsQuery, {
    variables: {
      graphId,
      timeFrom: `-${checksTimeRangeInSeconds[STATS_TIME_RANGE]}`,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const [updateConfigError, setUpdateConfigError] =
    useState<ApolloError | null>(null);

  const [updateCheckConfig] = useMutation<
    GraphQLTypes.UpdateCheckConfigMutation,
    GraphQLTypes.UpdateCheckConfigMutationVariables
  >(updateCheckConfigMutation, {
    onError: setUpdateConfigError,
  });

  const {
    values,
    errors,
    dirty,
    handleChange,
    handleSubmit,
    submitForm,
    setFieldValue,
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: data?.service?.checkConfiguration
      ? {
          includeBaseVariant:
            data.service.checkConfiguration.includeBaseVariant!,
          includedVariants: data.service.checkConfiguration.includedVariants!,
          operationCountThreshold:
            data.service.checkConfiguration.operationCountThreshold!,
          timeRangeSeconds: Number(
            data.service.checkConfiguration.timeRangeSeconds!,
          ),
          excludedClients: Array.isArray(
            data.service.checkConfiguration.excludedClients,
          )
            ? removeTypename(data.service.checkConfiguration.excludedClients!)
            : data.service.checkConfiguration.excludedClients,
          excludedOperationNames: Array.isArray(
            data.service.checkConfiguration.excludedOperationNames,
          )
            ? removeTypenameFromExcludedOperationNames(
                data.service.checkConfiguration.excludedOperationNames!,
              )
            : data.service.checkConfiguration.excludedOperationNames,
          downgradeStaticChecks:
            data.service.checkConfiguration.downgradeStaticChecks,
          downgradeDefaultValueChange:
            data.service.checkConfiguration.downgradeDefaultValueChange,
        }
      : {
          // these initializer values are never actually used,
          // but are here to make TS happy.
          // they will always get re-initialized when the check config exists,
          // or we throw if we don't get a config (below)
          includeBaseVariant: true,
          includedVariants: [],
          timeRangeSeconds: checksTimeRangeInSeconds[ChecksTimeRange.LastWeek],
          operationCountThreshold: 1,
          excludedClients: null,
          excludedOperationNames: null,
          downgradeStaticChecks: false,
          downgradeDefaultValueChange: false,
        },
    onSubmit: async (formValues) => {
      await updateCheckConfig({
        variables: {
          graphId,
          includedVariants: formValues.includedVariants,
          includeBaseVariant: formValues.includeBaseVariant,
          operationCountThreshold: formValues.operationCountThreshold,
          timeRangeSeconds: formValues.timeRangeSeconds,
          excludedClients: formValues.excludedClients,
          excludedOperationNames: formValues.excludedOperationNames,
          downgradeStaticChecks: formValues.downgradeStaticChecks,
          downgradeDefaultValueChange: formValues.downgradeDefaultValueChange,
        },
      });
    },
    validationSchema,
  });

  // diagnose validation errors
  if (Object.keys(errors).length > 0) {
    console.warn(errors); // eslint-disable-line no-console
  }

  const submitIfDirty = useCallback(() => {
    if (dirty && Object.keys(errors).length === 0) {
      submitForm();
    }
  }, [dirty, errors, submitForm]);

  useDebouncedEffect(submitIfDirty, AUTOSAVE_TIMEOUT_MS, [values]);

  const clearErrorsAndHandleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setUpdateConfigError(null);
      handleChange(e);
    },
    [setUpdateConfigError, handleChange],
  );

  if (loading || currentPlan.loading) {
    return <Loading />;
  }
  if ((error || currentPlan.error) && !data) {
    throw error || currentPlan.error;
  }
  if (!data?.service) {
    return <NotFound />;
  }
  if (!data.service.roles?.canQueryCheckConfiguration) {
    return (
      <div className="flex justify-center bg-silver-light">
        <div className="grow rounded-lg bg-white px-9 py-8 shadow">
          <PermissionBlock />
        </div>
      </div>
    );
  }
  if (!data.service.checkConfiguration) {
    throw new Error('expected a checks configuration');
  }

  const allVariants = sortByStatsData(
    data.service.variants.map((variant) => variant.name),
    statsData?.service?.operationCheckRequestsByVariant,
  );

  const variantStatsMap = buildStatsMap(
    statsData?.service?.operationCheckRequestsByVariant,
  );

  const roles = data?.service?.roles;

  return (
    <>
      <Form onSubmit={handleSubmit} className="space-y-4">
        {updateConfigError && <ErrorMessage />}
        <Card>
          <CardHeader>
            <CardTitle>Time range</CardTitle>
            <CardDescription>
              Checks for <em>{data.service.id}</em> will be run against
              operations from the specified time range:
            </CardDescription>
          </CardHeader>
          <CardBody>
            <div className="flex flex-col flex-wrap gap-2 md:flex-row">
              <PermissionWarning
                graphId={data.service.id}
                graphPermissions="canWriteCheckConfiguration"
                placement="top"
                childRenderer={(hasPermission: boolean) => (
                  <FormControl className="flex-1">
                    <FormLabel htmlFor="timeRangeSeconds" className="sr-only">
                      Time range selector
                    </FormLabel>
                    <Select
                      autoFocus={true}
                      id="timeRangeSeconds"
                      name="timeRangeSeconds"
                      value={values.timeRangeSeconds}
                      isDisabled={!hasPermission}
                      onChange={(e) => {
                        setFieldValue(
                          'timeRangeSeconds',
                          Number(e.target.value),
                        );
                      }}
                    >
                      {Object.values(ChecksTimeRange).map((timeRangeValue) => (
                        <option
                          key={`${checksTimeRangeInSeconds[timeRangeValue]}`}
                          value={`${checksTimeRangeInSeconds[timeRangeValue]}`}
                        >
                          Within the {timeRangeValue}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              {/* Operation count */}
              <PermissionWarning
                graphId={data.service.id}
                graphPermissions="canWriteCheckConfiguration"
                placement="top"
                childRenderer={(hasPermission: boolean) => (
                  <div className="flex flex-2 flex-col items-center gap-2 md:flex-row">
                    <div className="flex justify-center text-center">
                      <FormControl
                        isRequired
                        isInvalid={!!errors.operationCountThreshold}
                        className="flex-col md:flex-row"
                      >
                        <FormLabel htmlFor="operationCountThreshold">
                          Include operations that have run at least
                        </FormLabel>
                        <Input
                          className="w-24 text-center"
                          type="number"
                          name="operationCountThreshold"
                          isDisabled={!hasPermission}
                          onChange={clearErrorsAndHandleChange}
                          value={values.operationCountThreshold}
                          id="hoperationCountThreshold"
                        />
                        <FormErrorMessage className="justify-center">
                          {errors.operationCountThreshold}
                        </FormErrorMessage>
                      </FormControl>
                    </div>
                    <span>
                      {format.pluralize(
                        values.operationCountThreshold ?? 0,
                        'time',
                      )}
                      .
                    </span>
                  </div>
                )}
              />
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Included variants</CardTitle>
            <CardDescription>
              Operations reported for these variants will be checked against
              changes to the schema:
            </CardDescription>
          </CardHeader>
          <CardBody>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gridAutoRows: '1fr',
                gridColumnGap: '1rem',
                gridRowGap: '1rem',
                alignContent: 'start',
              }}
              className="text-base"
            >
              <VariantCheckbox
                key="includeBaseVariant"
                name="includeBaseVariant"
                isChecked={values.includeBaseVariant}
                value="true"
                title="Base variant (recommended)"
                description={
                  <Tooltip label="Include operations from the variant being checked against.">
                    <IconInfo className="mt-1" />
                  </Tooltip>
                }
                canWriteCheckConfiguration={
                  data.service.roles?.canWriteCheckConfiguration
                }
                onChange={clearErrorsAndHandleChange}
              />

              {allVariants.map((variantName) => (
                <VariantCheckbox
                  key={variantName}
                  name="includedVariants"
                  isChecked={values.includedVariants.includes(variantName)}
                  value={variantName}
                  title={`${graphId}@${variantName}`}
                  description={descriptionForVariant(
                    variantName,
                    statsLoading,
                    variantStatsMap,
                  )}
                  canWriteCheckConfiguration={
                    data.service?.roles?.canWriteCheckConfiguration
                  }
                  onChange={clearErrorsAndHandleChange}
                />
              ))}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Excluded clients</CardTitle>
            <CardDescription>
              Any operations sent by these clients will not be checked against
              changes to the schema. This is useful to filter out clients that
              are used to run ad hoc operations.
            </CardDescription>
            <PermissionWarning
              graphId={data.service.id}
              graphPermissions="canWriteCheckConfiguration"
              childRenderer={(hasPermission) => (
                <Button
                  as={Link}
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.configureExcludedClients,
                  })}
                  isDisabled={!hasPermission}
                >
                  Configure
                </Button>
              )}
            />
          </CardHeader>
          <CardBody>
            <ExcludedClientsTable
              canViewVersions={currentPlan.currentPlan?.clientVersions ?? false}
              graphId={graphId}
              excludedClients={values.excludedClients}
              onExcludedClientRemove={(clientName: string) => {
                setFieldValue(
                  'excludedClients',
                  values.excludedClients?.filter(
                    (client) => client.name !== clientName,
                  ),
                );
              }}
              showActions={true}
            />
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Excluded operations</CardTitle>
            <CardDescription>
              Any operation names sent with these names will not be checked
              against changes to the schema. This is useful to filter out known
              and expected operations that raise flags during schema checks.
            </CardDescription>
          </CardHeader>
          <CardBody>
            <ExcludedOperationsTable
              graphId={graphId}
              excludedOperationNames={values.excludedOperationNames}
              onNewExcludedOperationName={(name: string) => {
                const newOperations = [];
                if (values.excludedOperationNames?.length) {
                  newOperations.push(...values.excludedOperationNames);
                }
                newOperations.push({ name });
                setFieldValue('excludedOperationNames', newOperations);
              }}
              onExcludedOperationNameRemove={(filterName: string) => {
                setFieldValue(
                  'excludedOperationNames',
                  values.excludedOperationNames?.filter(
                    ({ name }) => name !== filterName,
                  ),
                );
              }}
            />
          </CardBody>
        </Card>
        {shouldShowDowngradeChecksConfig && (
          <IgnoredConditionsCard
            downgradeStaticChecks={values.downgradeStaticChecks ?? false}
            downgradeDefaultValueChange={
              values.downgradeDefaultValueChange ?? false
            }
            isDisabled={!roles.canWriteCheckConfiguration}
            canWriteCheckConfiguration={roles.canWriteCheckConfiguration}
            onChangeDowngradeStaticChecks={(value) =>
              setFieldValue('downgradeStaticChecks', value)
            }
            onChangeDowgradeDefaultValueChange={(value) =>
              setFieldValue('downgradeDefaultValueChange', value)
            }
          />
        )}
      </Form>
      <URLModal name={Config.modals.configureExcludedClients}>
        {({ isOpen, closeModal }) => (
          <ConfigureExcludedClientsModal
            isOpen={isOpen}
            graphId={graphId}
            showClientsFromTime={
              `-${checksTimeRangeInSeconds[STATS_TIME_RANGE]}` as const
            }
            excludedClients={values.excludedClients}
            onUpdate={(excludedClients) => {
              setUpdateConfigError(null);
              setFieldValue('excludedClients', excludedClients);
              closeModal();
            }}
            onClose={closeModal}
          />
        )}
      </URLModal>
    </>
  );
};

export const descriptionForVariant = (
  name: string,
  statsLoading: boolean,
  checkStats: Map<
    string,
    GraphQLTypes.ChecksConfigPageStatsQuery_service_operationCheckRequestsByVariant
  >,
) => {
  const { cachedRequestsCount, uncachedRequestsCount } =
    checkStats.get(name || 'current') || {};
  const requestCount =
    (cachedRequestsCount || 0) + (uncachedRequestsCount || 0);
  return statsLoading ? (
    <Spinner className="ml-3" size="tiny" />
  ) : requestCount ? (
    <span>
      <b className="text-neutral">{format.requests(requestCount)}</b> reqs{' '}
      {STATS_TIME_RANGE}
    </span>
  ) : (
    `--`
  );
};
